import ArrowIco from "../../../../assets/careerBuddy/arrow_ic.svg";
import MaximizeIco from "../../../../assets/careerBuddy/maximize_ico.svg";
import MinimizeIco from "../../../../assets/careerBuddy/minimize_ico.svg";
import SendIco from "../../../../assets/careerBuddy/send_ic.svg";
import ReadCheckIco from "../../../../assets/careerBuddy/ic_read_check.svg";
import remarkGfm from "remark-gfm";
import "./style.scss";

import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  addDateChat,
  clearMsgCompanions,
  initChatHistory,
  initCompanions,
  removeChatHistoryLoaded,
  sendMsgCompanions,
  updateChatFeedback,
} from "../../../../redux/actions/aiCompanions";
import { useState, useEffect, useRef, useMemo } from "react";
import CopyIco from "../../../../assets/copy_ico.svg";
import { copyToClipboard } from "../../../../common/utils";
import { buildChatApiHistory } from "../../../../common/chatHistory";
import LoaderTyping from "../../../../components/Loader/loaderTyping";
import ReactMarkdown from "react-markdown";
import ErrorMsg from "../../../../components/ErrorMsg";
import useRecommendations from "../../../../hooks/useRecommendations";
import { ThumbsIco } from "../../../../components/icons";

const assistantMarkdownComponents = {
  a: ({ node, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-4">
      <table className="min-w-full border-collapse">
        {children}
      </table>
    </div>
  ),
};

/** Render markdown on every stream update so completed syntax (e.g. `**bold**`) formats immediately. */
function AssistantMessageBody({ text, streaming }) {
  return (
    <div
      className={`table-renderer__markdown${
        streaming ? " table-renderer__markdown--streaming" : ""
      }`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={assistantMarkdownComponents}
      >
        {text ?? ""}
      </ReactMarkdown>
    </div>
  );
}

export default function AiCompanionsDetail() {
  const { profile } = useSelector((state) => state.profileReducer);
  const { user } = useOutletContext();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("type");
  const router = useNavigate();
  const dispatch = useDispatch();
  const {
    chatHistory,
    isTypingAi,
    isChatLoad,
    dateAdded,
    errorMsg,
    isChatHistoryLoaded,
  } = useSelector((state) => state.aiCompanionsReducer);
  const { aiAgents } = useSelector((state) => state.aiCompanionsReducer);
  const { results } = useRecommendations();
  const { id } = useParams();
  const [text, setText] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsgLocal, setErrorMsg] = useState("");
  const [displayInfo, setDisplayInfo] = useState({});
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [typingIndex, setTypingIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [isMaxMinmize, setIsMaxMinmize] = useState(false);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  const chatContainerRef = useRef(null);
  const hasSentMessage = useRef(false);
  const hasHitAgentApi = useRef(false);
  const textareaRef = useRef(null);
  const promptSent = useRef(false);
  const typingInterval = useRef();

  /** Merge last assistant from Redux so streaming text, message_id, and feedback stay in sync (thumbs need message_id). */
  const displayForRender = useMemo(() => {
    const last = chatHistory[chatHistory.length - 1];
    if (last?.type === "assistant") {
      const out = [...displayedMessages];
      if (out.length && out[out.length - 1]?.type === "assistant") {
        out[out.length - 1] = { ...out[out.length - 1], ...last };
        return out;
      }
      if (out.length && out[out.length - 1]?.type === "user") {
        out.push(last);
        return out;
      }
    }
    return displayedMessages;
  }, [chatHistory, displayedMessages]);

  useEffect(() => {
    if (
      query?.length &&
      results?.length &&
      isChatHistoryLoaded &&
      !promptSent.current
    ) {
      if (query === "compare-careers") {
        handleDispatch(
          `I would like to compare the following careers: ${getInterestedCareers()}`,
        );
        promptSent.current = true;
        dispatch(removeChatHistoryLoaded({ isChatHistoryLoaded: false }));
      } else if (query === "start-action-plan") {
        handleDispatch(
          `I would like to create an Action Plan for the following careers: ${getInterestedCareers()}`,
        );
        promptSent.current = true;
        dispatch(removeChatHistoryLoaded({ isChatHistoryLoaded: false }));
      } else if (query === "find-schools") {
        handleDispatch(
          `I would like to explore schools and programs related to these careers: ${getInterestedCareers()}`,
        );
        promptSent.current = true;
        dispatch(removeChatHistoryLoaded({ isChatHistoryLoaded: false }));
      }
    }
  }, [query, results, isChatHistoryLoaded, promptSent.current]);

  const getCareersPrompt = () => {
    if (query === "compare-careers") {
      return `who wants to compare certain careers. Help them understand key differences by considering factors like required education, average salary, job outlook, and work-life balance. Present the comparison in a simple side-by-side or chart format to make it easy to read and reflect on.`;
    } else if (query === "start-action-plan") {
      return `who wants to learn more about or prepare for certain careers. Create an action plan table with columns: Career, Short-Term Steps (0–6 months), Medium-Term Steps (6–12 months), and Long-Term Steps (12+ months). Steps should be realistic, age-appropriate, and, when possible, reflect opportunities that may vary by location (such as local programs, virtual resources, or common regional pathways).`;
    } else if (query === "find-schools") {
      return `who is exploring schools and programs related to certain careers. Help them find relevant programs, certifications, or degrees aligned with those careers. Present the results in a table format with clear columns such as Career, Type of Program, Typical Length, Credential Earned, Cost Estimate, and Example Schools or Providers to make comparison easy.`;
    }
    return "";
  };
  const getInterestedCareers = () => {
    const titles = results
      .filter((item) => item.interested)
      .map((item) => item.unique_title)
      ?.join(", ");
    return titles || "";
  };
  useEffect(() => {
    return () => {
      promptSent.current = false;
      clearTimeout(typingInterval.current);
      dispatch(clearMsgCompanions());
    };
  }, []);
  useEffect(() => {
    if (textareaRef.current && !(isTypingAi || isTyping)) {
      textareaRef.current.focus();
    }
  }, [isTypingAi, isTyping]);
  useEffect(() => {
    if (aiAgents?.results?.length) {
      setDisplayInfo(aiAgents?.results?.filter((item) => item.id === +id)[0]);
    } else if (!hasHitAgentApi.current) {
      dispatch(initCompanions({ page: 1, per_page: 50 }));
      hasHitAgentApi.current = true;
    }
  }, [aiAgents?.results, id]);
  useEffect(() => {
    if (profile?.first_name && !hasSentMessage.current) {
      dispatch(
        initChatHistory({
          id: id,
          first_msg: getFirstMsg(),
          meta_title: query?.length ? true : false,
        }),
      );
      // dispatch(sendMsgCompanions({ id: id, text: profile?.first_name, hitFirstTime: true }));
      hasSentMessage.current = true; // Mark as executed
    }
  }, [profile?.first_name]);
  useEffect(() => {
    if (!isTyping && !isChatLoad && typingIndex < chatHistory.length) {
      const message = chatHistory[typingIndex];
      if (message?.type === "assistant" && message?.streaming) {
        return;
      }
      if (
        message?.type === "assistant" &&
        !message?.streaming &&
        message?.message_id &&
        typingIndex === chatHistory.length - 1
      ) {
        setDisplayedMessages((prev) => {
          if (prev[prev.length - 1]?.type === "assistant") {
            const next = [...prev];
            next[next.length - 1] = { ...message, streaming: false };
            return next;
          }
          return [...prev, { ...message, streaming: false }];
        });
        setTypingIndex((prev) => prev + 1);
        return;
      }
      if (message?.type === "assistant") {
        const useTypingAnimation =
          message.text.length < 800 &&
          !message.text.includes("|") &&
          (message.text.match(/\n/g) || []).length < 8;
        if (!useTypingAnimation) {
          setDisplayedMessages((prev) => [...prev, message]);
          setTypingIndex((prev) => prev + 1);
          setIsTyping(false);
          return;
        }
        setIsTyping(true);
        let i = 0;
        let typedMessage = "";
        const typeChar = () => {
          typedMessage += message.text[i];
          setDisplayedMessages((prev) => {
            const updatedMessages = [...prev];
            updatedMessages[updatedMessages.length - 1] = {
              ...message,
              text: typedMessage,
            };
            return updatedMessages;
          });
          i++;
          if (i < message.text.length) {
            typingInterval.current = setTimeout(typeChar, 15);
          } else {
            setErrorMsg("");
            setTypingIndex((prev) => prev + 1);
            setIsTyping(false);
          }
        };
        setDisplayedMessages((prev) => [...prev, { ...message, text: "" }]);
        typeChar();
      } else {
        setDisplayedMessages((prev) => [...prev, message]);
        setTypingIndex((prev) => prev + 1);
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
      textareaRef.current?.focus();
    } else if (isChatLoad && chatHistory.length) {
      setTypingIndex(chatHistory?.length);
      setDisplayedMessages(chatHistory);
    }
  }, [
    chatHistory,
    typingIndex,
    isTyping,
    isChatLoad,
    displayedMessages.length,
  ]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!(isTypingAi || isTyping)) {
        handleSend(e);
      }
    }
  };

  const handleDispatch = (prompt = "") => {
    textareaRef.current.style.height = "45px"; // Reset height
    const params = {
      id: id,
      text: prompt
        ? prompt
        : text.length > 5000
          ? text.substring(0, 5000)
          : text,
      role: "user",
      first_msg: getFirstMsg(getCareersPrompt()),
      history: buildChatApiHistory(chatHistory),
    };

    dispatch(sendMsgCompanions(params));
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (text.trim().length > 5000) {
      setErrorMsg("Message trimmed to fit the 5000-character limit.");
    }
    if (text.trim().length) {
      if (!dateAdded) {
        dispatch(addDateChat());
      }
      setText("");
      // textareaRef.current.style.height = "45px"; // Reset height

      handleDispatch();
    }
  };
  const getFirstMsg = (attachMessage = "") => {
    return `You’re chatting with ${profile?.first_name}, a ${
      profile?.age
    }-year-old from ${profile?.city ? profile?.city : ""}${
      profile?.state ? ", " + profile?.state : ""
    }${attachMessage ? " " + attachMessage : ""}.`;
  };
  const handleInput = (event) => {
    const textarea = event.target;
    // textareaRef.current = textarea
    textarea.style.height = "45px"; // Reset height
    const newHeight = Math.min(textarea.scrollHeight, 200); // Max height 200px
    textarea.style.height = `${newHeight}px`;
  };
  const copyMsg = async (htmlElement, index) => {
    copyToClipboard(htmlElement);
    setCopiedMessageIndex(index); // Store the copied message index
    setCopyMessage("Copied successfully!");
    setTimeout(() => {
      setCopyMessage("");
    }, 3000);
  };
  const formatTime = () => {
    const date = new Date();
    let hours = date.getHours();
    let minutes = date.getMinutes();
    let ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // Convert 0 to 12
    minutes = minutes < 10 ? "0" + minutes : minutes; // Ensure two-digit minutes
    return `${hours}:${minutes}${ampm}`;
  };
  const handleClearMessage = () => {
    dispatch(clearMsgCompanions());
    setDisplayedMessages([]);
    setTypingIndex(0);
    setIsTyping(false);
  };
  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;
    requestAnimationFrame(() => {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    });
  }, [displayForRender, isTypingAi]);
  const handleMaxMinimize = () => {
    setIsMaxMinmize(!isMaxMinmize);
  };

  return (
    <div
      className={`aiCompanions__container ${
        isMaxMinmize ? "fullContainer" : ""
      }`}
    >
      <div className="aiCompanions__backScoop">
        <button onClick={() => router(-1)}>
          <img src={ArrowIco} alt="back" /> Back
        </button>
        <button onClick={handleMaxMinimize}>
          <img
            src={!isMaxMinmize ? MaximizeIco : MinimizeIco}
            alt="back"
            title={`${isMaxMinmize ? "Minimize" : "Maximize"} Chat`}
            width={20}
          />{" "}
        </button>
      </div>
      <div className="aiCompanions__title">
        <div className="d-flex align-items-end">
          <h3>{displayInfo?.name}</h3>
          <img src={displayInfo?.image} height={54} />
        </div>
        <p>{displayInfo?.short_description}</p>
        <div className="aiCompanions__chat">
          <div className="aiCompanions__chat__container" ref={chatContainerRef}>
            {displayForRender.length ? (
              displayForRender.map((item, key) =>
                item.type === "date" ? (
                  <div key={key} className={"aiCompanions__chat__user"}>
                    <div className="time">{item.text}</div>
                  </div>
                ) : item.type === "assistant" &&
                  !String(item.text ?? "").trim() ? null : (
                  <div
                    key={key}
                    className={`${
                      item.type === "user"
                        ? "aiCompanions__chat__user"
                        : "aiCompanions__chat__support"
                    }`}
                  >
                    <div
                      className={`d-flex align-items-end ${
                        item.type === "user" ? "justify-content-end" : ""
                      }`}
                    >
                      <div className="message">
                        {item.type === "user" ? (
                          item.text
                        ) : (
                          <AssistantMessageBody
                            text={item.text}
                            streaming={Boolean(item.streaming)}
                          />
                        )}
                      </div>
                      {item.type === "user" ? (
                        <img src={ReadCheckIco} alt="check" />
                      ) : (
                        <MessageBtns
                          isLoading={isLoading}
                          copyMsg={copyMsg}
                          item={item}
                          key={key}
                          copyMessage={copyMessage}
                          copiedMessageIndex={copiedMessageIndex}
                          setDisplayedMessages={setDisplayedMessages}
                          dispatch={dispatch}
                          id={id}
                          className="desktop-show"
                        />
                      )}
                    </div>
                    {item.type === "assistant" && (
                      <div className="d-flex justify-between">
                        <div className="time">Sent {formatTime()}</div>
                        <MessageBtns
                          isLoading={isLoading}
                          copyMsg={copyMsg}
                          item={item}
                          key={key}
                          copyMessage={copyMessage}
                          copiedMessageIndex={copiedMessageIndex}
                          setDisplayedMessages={setDisplayedMessages}
                          dispatch={dispatch}
                          id={id}
                          className="mobile-show"
                        />
                      </div>
                    )}
                  </div>
                ),
              )
            ) : (
              <p className="text-center">Chat not started yet</p>
            )}
            {isTypingAi ? (
              <div className="aiCompanions__chat__support">
                <div className="message d-inline-flex">
                  <LoaderTyping />
                </div>
              </div>
            ) : null}
          </div>
          <div>
            {displayedMessages?.length ? (
              <button
                className="aiCompanions__chat__clearMessage"
                onClick={handleClearMessage}
              >
                Clear Messages
              </button>
            ) : null}
            <ErrorMsg errorMsg={errorMsgLocal || errorMsg} />
            <form className="CareerCont__chatbot__input">
              <textarea
                ref={textareaRef}
                disabled={isTypingAi || isTyping}
                onInput={handleInput}
                value={text}
                onKeyDown={handleKeyDown}
                onChange={(e) => setText(e.target.value)}
                placeholder="Start chatting ..."
              />
              {!(isTypingAi || isTyping) ? (
                <button onClick={handleSend}>
                  <img src={SendIco} className="send_ico" alt="send" />
                </button>
              ) : null}
            </form>
            <p className="aiCompanions__chat__note">
              AI Companions may make mistakes. Please verify key information or
              consult a trusted professional.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
function MessageBtns({
  isLoading,
  copyMsg,
  item,
  key,
  copyMessage,
  copiedMessageIndex,
  setDisplayedMessages,
  dispatch,
  id,
  className,
}) {
  return (
    <div className={`aiCompanions__chat__btns ${className}`}>
      <button disabled={isLoading} onClick={() => copyMsg(item.text, key)}>
        {copyMessage?.length && copiedMessageIndex === key ? (
          <img src={ReadCheckIco} alt="copy" />
        ) : (
          <img src={CopyIco} alt="copy" />
        )}
      </button>
      {item.message_id && (
        <>
          <button
            type="button"
            className={`thumbs-up ${item.feedback === "up" ? "thumbs-active" : ""}`}
            onClick={() => {
              setDisplayedMessages((prev) =>
                prev.map((m, i) => (i === key ? { ...m, feedback: "up" } : m)),
              );
              dispatch(
                updateChatFeedback({
                  agentId: id,
                  message_id: item.message_id,
                  feedback: "up",
                }),
              );
            }}
            title="Thumbs up"
            aria-label="Good response"
          >
            <ThumbsIco />
          </button>
          <button
            type="button"
            className={`${item.feedback === "down" ? "thumbs-active" : ""}`}
            onClick={() => {
              setDisplayedMessages((prev) =>
                prev.map((m, i) =>
                  i === key ? { ...m, feedback: "down" } : m,
                ),
              );
              dispatch(
                updateChatFeedback({
                  agentId: id,
                  message_id: item.message_id,
                  feedback: "down",
                }),
              );
            }}
            title="Thumbs down"
            aria-label="Poor response"
          >
            <ThumbsIco />
          </button>
        </>
      )}
    </div>
  );
}
